import { useT } from '../i18n';
import { formatTonExact } from '../lib/ton-format';
import type { CashDisplay, ChipsDisplay } from '../lib/balance-surface';

/**
 * CF-BALANCE-VISIBILITY-V1 Surface A — one labelled money fact in the wallet menu.
 *
 * ⭐ WHY THIS IS A COMPONENT AND NOT TWO BLOCKS OF JSX.
 * PL's requirement is not "show both numbers", it is that a player can add them at a glance
 * (#13670: "How the fck can a player understand he has 10 ton to play with if he only see 5 TON
 * on the top"). That makes EQUAL PROMINENCE the actual spec. Two hand-written columns satisfy it
 * on the day they are written and drift the first time someone restyles one of them. Rendering
 * both through a single component makes the property structural: the type scale, weight and
 * numeric alignment cannot diverge, because there is only one of each.
 *
 * ⛔ It renders a NUMBER only from a kind that carries one. `establishing` and `unavailable` have
 * no numeric field at all in `balance-surface.ts`, so this component is physically unable to print
 * a 0 it does not know — the module's one invariant, inherited rather than re-implemented.
 *
 * `hidden` is excluded at the type level rather than handled and ignored. A branch that cannot be
 * reached is a branch nothing can test; the caller must narrow it away, and the type-checker makes
 * that non-optional.
 */
export interface WalletMoneyCellProps {
    label: string;
    /** Gold marks bonus chips as a different KIND of money, not a lesser amount. */
    tone: 'cash' | 'chips';
    display: Exclude<CashDisplay | ChipsDisplay, { kind: 'hidden' }>;
}

export function WalletMoneyCell({ label, tone, display }: WalletMoneyCellProps) {
    const t = useT();
    const accent = tone === 'chips' ? 'var(--gold)' : 'var(--text-primary)';
    const nano = display.kind === 'known' || display.kind === 'amount' ? display.nano : null;

    return (
        <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium truncate"
               style={{ color: tone === 'chips' ? 'var(--gold)' : 'var(--text-muted)' }}>
                {label}
            </p>
            {nano === null ? (
                <p className="text-[11px] font-semibold leading-7" style={{ color: 'var(--text-muted)' }}>
                    {display.kind === 'establishing'
                        ? t('app.wallet.checking_session')
                        : t('app.wallet.balance_unavailable')}
                </p>
            ) : (
                <p className="text-lg font-black leading-7"
                   style={{ color: accent, fontVariantNumeric: 'tabular-nums' }}>
                    {formatTonExact(nano)}{' '}
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                        {t('common.ton_unit')}
                    </span>
                </p>
            )}
        </div>
    );
}
